import {
  BONUS,
  SESSION_KEYS,
  USER_PROFILE_DATA,
  SITE_TECHNICAL_DATA,
  UI_FLOW_PREFERENCES,
} from "src/consts/session";
import {
  contractSummaryWithSmartMeterFormSchema,
  contractSummaryWithoutSmartMeterFormSchema,
} from "src/schema/contractSummaryFormSchema";
import { useForm } from "react-hook-form";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Text from "src/components/Displays/Text";
import { SmartMeterTypes } from "../SmartMeter";
import { Tariff } from "src/models/clientsModel";
import { Box, Stack, Divider } from "@mui/material";
import { dateFormat } from "src/helpers/dateFormat";
import { Trans, useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import anonymizeIBAN from "src/helpers/anonymizeIBAN";
import { PaymentDetailsTypes } from "../PaymentDetails";
import { CancelContractTypes } from "../ContractChange";
import PageTitle from "src/components/Displays/PageTitle";
import InputLabel from "src/components/Displays/InputLabel";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import { SELF_ONBOARDING_PATHS } from "src/consts/Routes/SelfOnboarding";
import ContractSummaryForm from "src/components/Forms/ContractSummaryForm";
import FormSubmitContainer from "src/components/Shared/FormSubmitContainer";
import TarifSelectionContainer from "../Tarif/components/TarifSelectionContainer";
import useGetContractSummaryData from "src/pages/ContractSummary/useGetContractSummaryData";
import { OEMs } from "src/hooks/useLeadTransfer";
import { ROUTES } from "src/consts/routes";

export interface ContractSummaryFormValues {
  wallet: boolean;
  smartMeter?: boolean;
}

const ContractSummary = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [showTariffTimeline] = useState(true);

  const [userProfileDataFromSession] = useSessionStorage<USER_PROFILE_DATA>(
    SESSION_KEYS.USER_PROFILE_DATA,
  );

  const [getSessionStorageItem] = useSessionStorage<Tariff[]>(
    SESSION_KEYS.TARIFFS,
  );

  const [getUiFlowPreferences] = useSessionStorage<UI_FLOW_PREFERENCES>(
    SESSION_KEYS.UI_FLOW_PREFERENCES,
  );

  const [getOemSelected] = useSessionStorage<string>(
    SESSION_KEYS.SELF_ONBOARDING_OEM_SELECTED,
  );

  const [getBonus] = useSessionStorage<BONUS | null>(SESSION_KEYS.BONUS);
  const showBonusCampaignFooter = getBonus()?.isActive ?? false;

  const { tariffsData } = useMemo(() => {
    const tariffsData = getSessionStorageItem();
    return {
      tariffsData: tariffsData,
    };
  }, [getSessionStorageItem]);

  const savingsData = tariffsData && tariffsData[0];
  const monthlyCosts =
    (savingsData && savingsData.fixTariff?.monthlyCosts) ?? "";
  const yearlyCosts = (savingsData && savingsData.fixTariff?.yearlyCosts) ?? "";
  const yearlyGridSupplyKwh =
    userProfileDataFromSession()?.yearlyGridSupplyKwh ?? "";

  const bonusEndDate = getBonus()?.endDate ?? "";

  const footnote1Text = t("page.tariff.footnotes.footnote1");
  const footnote2Text = t("page.tariff.footnotes.footnote2", {
    yearlyGridSupplyKwh,
    monthlyCosts,
    yearlyCosts,
  });

  const footnote4Text = t("page.tariff.footnotes.footnote4", {
    bonusEndDate: dateFormat(bonusEndDate),
  });

  const footnoteLastText = t("page.tariff.footnotes.last");

  const handleSubmit = async () => {
    const uiFlowPreferences = getUiFlowPreferences();
    const oemSelected = getOemSelected();

    if (
      uiFlowPreferences &&
      uiFlowPreferences.skipOemAuth &&
      uiFlowPreferences.skipOemAuth === "false" &&
      uiFlowPreferences.processType === "SITE_FULL_SELF_V1" &&
      oemSelected === OEMs.SMA_HOMEMGR
    ) {
      navigate(SELF_ONBOARDING_PATHS.ACCESS);
      return;
    }

    navigate(ROUTES.CONTRACT_SUMMARY);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const [technicalData] = useSessionStorage<SITE_TECHNICAL_DATA>(
    SESSION_KEYS.SITE_TECHNICAL_DATA,
  );
  const isSmartMeterSelected =
    technicalData()?.smartMeterInstalled === SmartMeterTypes.JA;

  const form = useForm<ContractSummaryFormValues>({
    mode: "onSubmit",
    defaultValues: {
      smartMeter: false,
      wallet: false,
    },
    resolver: yupResolver(
      isSmartMeterSelected
        ? contractSummaryWithSmartMeterFormSchema
        : contractSummaryWithoutSmartMeterFormSchema,
    ),
  });

  const {
    tariffProviderDisplayName,
    tariffDisplayName,
    street,
    houseNumber,
    zipCode,
    city,
    mobileNumber,
    email,
    marketLocationID,
    meterNumber,
    cancelContractType,
    energyProvider,
    contractStartDate,
    paymentAccout,
    cardHolder,
    iban,
  } = useGetContractSummaryData();

  return (
    <>
      <Stack
        sx={{
          my: 5,
        }}
      >
        <PageTitle text={t("page.contractSummary.doneBusiness")} />
      </Stack>

      <Box height={12} />

      <Stack gap={4}>
        <Stack>
          <Text
            sx={(theme) => ({
              color: theme.palette.app.primaryFont,
              fontSize: "20px",
              fontWeight: "600",
              lineHeight: "26px",
              fontFamily: "isonType",
              letterSpacing: "-0.4px",
              mb: 4,
            })}
            text={t("page.contractSummary.yourInputs")}
          />
        </Stack>
        <Stack gap={2}>
          <InputLabel text={t("page.contractSummary.newEnergyProductTitle")} />
          <Text
            text={t("page.contractSummary.newEnergyProduct", {
              tariffDisplayName,
              tariffProviderDisplayName,
            })}
          />
        </Stack>
        <Stack>
          <Stack gap={2}>
            <InputLabel text={t("page.contractSummary.deliveryAddress")} />
            <Text
              text={t("page.contractSummary.deliveryAddressDetails1", {
                street,
                houseNumber,
              })}
            />
          </Stack>
          <Text
            text={t("page.contractSummary.deliveryAddressDetails2", {
              zipCode,
              city,
            })}
          />
        </Stack>
        <Stack>
          <Stack gap={2}>
            <InputLabel
              text={t("page.contractSummary.contactInformationHeader")}
            />
            <Text text={mobileNumber} />
          </Stack>
          <Text text={email} />
        </Stack>
        <Stack>
          <Stack gap={2}>
            <InputLabel text={t("page.contractSummary.marketLocationId")} />
            <Text text={marketLocationID} />
          </Stack>
        </Stack>
        <Stack>
          <Stack gap={2}>
            <InputLabel text={t("page.contractSummary.meterNumber")} />
            <Text text={meterNumber} />
          </Stack>
        </Stack>
        <Stack>
          <Stack gap={2}>
            <InputLabel
              text={t("page.contractSummary.terminationOfContract")}
            />
            <Text
              text={
                cancelContractType === CancelContractTypes.AUTO
                  ? t("page.contractSummary.byUtility", { energyProvider })
                  : t("page.contractSummary.byUser")
              }
            />
          </Stack>
        </Stack>
        <Stack>
          <Stack gap={2}>
            <InputLabel
              text={t("page.contractSummary.startElectricityContract")}
            />
            <Text text={dateFormat(contractStartDate)} />
          </Stack>
        </Stack>
        <Stack>
          <Stack gap={2}>
            <InputLabel text={t("page.contractSummary.paymentMethod")} />
            {paymentAccout === PaymentDetailsTypes.SEPA ? (
              <Text text={cardHolder} />
            ) : (
              <Text text={t("page.contractSummary.bankTransfer")} />
            )}
          </Stack>
          {paymentAccout === PaymentDetailsTypes.SEPA && (
            <Text text={anonymizeIBAN(iban)} />
          )}
        </Stack>

        <Divider />

        <Stack my={-6}>
          <TarifSelectionContainer
            showTrustBadges={false}
            showSavingsResult={true}
            tariffsData={tariffsData}
            showTariffTimeline={showTariffTimeline}
            topTextKey="globalContext.order"
          />
        </Stack>

        <Divider />

        <Box
          component="form"
          autoComplete="off"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <ContractSummaryForm
            form={form}
            isSmaSystemNotConnected={true}
            isSmartMeterSelected={isSmartMeterSelected}
          />
          <FormSubmitContainer
            onClick={handleBack}
            submitButtonText={"Anlage verbinden"}
          />
        </Box>
        <Stack gap={4}>
          <Text
            sx={(theme) => ({
              color: "#848F9F",
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "18px",
            })}
            text={footnote1Text}
          />
          <Text
            sx={(theme) => ({
              color: "#848F9F",
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "18px",
            })}
            text={footnote2Text}
          />
          <Box
            sx={(theme) => ({
              color: "#848F9F",
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "18px",
              "& a": {
                color: "the",
                textDecoration: "none",
              },
              "& a:hover": {
                textDecoration: "underline",
              },
            })}
          >
            <Trans
              i18nKey="page.tariff.footnotes.footnote3"
              components={{
                1: (
                  <a
                    href="https://www.lichtblick.de/oekostrom/stromkennzeichnung/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="View electricity labeling"
                  />
                ),
              }}
            />
          </Box>
          {showBonusCampaignFooter && (
            <Text
              sx={{
                color: "#848F9F",
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "18px",
              }}
              text={footnote4Text}
            />
          )}
          <Text
            sx={(theme) => ({
              color: "#848F9F",
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "18px",
            })}
            text={footnoteLastText}
          />
        </Stack>
      </Stack>
    </>
  );
};

export default ContractSummary;
